<template>
  <div class="docs-page">
    <aside class="docs-sidebar" :class="{ open: sidebarOpen }">
      <div class="docs-sidebar-header">
        <router-link to="/" class="logo"></router-link>
        <span class="docs-badge badge badge-green">API Docs</span>
      </div>
      <nav class="docs-nav">
        <a v-for="section in navSections" :key="section.id" :href="'#' + section.id" class="docs-nav-item" :class="{ active: activeSection === section.id }" @click="sidebarOpen = false">
          <span class="nav-icon" :class="section.icon"></span>
          {{ section.label }}
        </a>
      </nav>
      <div class="docs-sidebar-footer">
        <a :href="'https://dashboard.wizzo.stream'" target="_blank" class="btn" style="width:100%;text-align:center;justify-content:center;">כניסה לדשבורד</a>
      </div>
    </aside>

    <div class="docs-overlay" :class="{ open: sidebarOpen }" @click="sidebarOpen = false"></div>

    <main class="docs-main">
      <div class="docs-topbar hide-desktop">
        <button class="docs-menu-btn" @click="sidebarOpen = !sidebarOpen"><span class="fas fa-bars"></span></button>
        <span class="docs-topbar-title">API Documentation</span>
      </div>

      <div class="docs-content">

        <!-- Introduction -->
        <section id="introduction" class="docs-section">
          <h1>WizzoStream API</h1>
          <p class="docs-intro">ה-API של WizzoStream מאפשר לכם להעלות, לנהל ולהזרים סרטונים ישירות מהקוד שלכם. API פשוט מבוסס REST עם אותנטיקציה באמצעות API Key.</p>
          <div class="docs-info-box">
            <div class="info-row">
              <span class="info-label">Base URL</span>
              <code class="info-value">https://api.wizzo.stream/api_wizzocms</code>
            </div>
            <div class="info-row">
              <span class="info-label">Authentication</span>
              <code class="info-value">Authorization: Bearer YOUR_API_KEY</code>
            </div>
            <div class="info-row">
              <span class="info-label">Content Type</span>
              <code class="info-value">application/json / multipart/form-data</code>
            </div>
          </div>
        </section>

        <!-- Authentication -->
        <section id="authentication" class="docs-section">
          <h2>אימות (Authentication)</h2>
          <p>כל הבקשות ל-API דורשות מפתח API שנשלח בכותרת <code>Authorization</code>. ניתן ליצור ולנהל מפתחות API מתוך <a href="https://dashboard.wizzo.stream/api" target="_blank">דשבורד הניהול</a>.</p>
          <div class="docs-code-section">
            <div class="code-tabs">
              <span class="code-tab" :class="{ active: authTab === 'js' }" @click="authTab = 'js'">JavaScript</span>
              <span class="code-tab" :class="{ active: authTab === 'php' }" @click="authTab = 'php'">PHP</span>
              <span class="code-tab" :class="{ active: authTab === 'python' }" @click="authTab = 'python'">Python</span>
              <span class="code-tab" :class="{ active: authTab === 'curl' }" @click="authTab = 'curl'">cURL</span>
            </div>
            <pre class="code-block" v-show="authTab === 'js'"><span class="token-keyword">const</span> <span class="token-variable">headers</span> = {
  <span class="token-string">'Authorization'</span>: <span class="token-string">'Bearer YOUR_API_KEY'</span>,
  <span class="token-string">'Content-Type'</span>: <span class="token-string">'application/json'</span>
};</pre>
            <pre class="code-block" v-show="authTab === 'php'"><span class="token-variable">$headers</span> = [
  <span class="token-string">'Authorization: Bearer YOUR_API_KEY'</span>,
  <span class="token-string">'Content-Type: application/json'</span>
];</pre>
            <pre class="code-block" v-show="authTab === 'python'"><span class="token-variable">headers</span> = {
  <span class="token-string">'Authorization'</span>: <span class="token-string">'Bearer YOUR_API_KEY'</span>,
  <span class="token-string">'Content-Type'</span>: <span class="token-string">'application/json'</span>
}</pre>
            <pre class="code-block" v-show="authTab === 'curl'"><span class="token-function">curl</span> -H <span class="token-string">'Authorization: Bearer YOUR_API_KEY'</span> \
  -H <span class="token-string">'Content-Type: application/json'</span> \
  <span class="token-string">https://api.wizzo.stream/api_wizzocms/...</span></pre>
          </div>
        </section>

        <!-- Upload Video -->
        <section id="upload" class="docs-section">
          <h2>העלאת סרטון</h2>
          <div class="endpoint-header">
            <span class="method-badge post">POST</span>
            <code class="endpoint-url">/video_manager/upload</code>
          </div>
          <p>העלאת קובץ וידאו חדש. השתמשו ב-<code>multipart/form-data</code> לשליחת הקובץ.</p>
          
          <h4>פרמטרים</h4>
          <table class="params-table">
            <thead><tr><th>שם</th><th>סוג</th><th>חובה</th><th>תיאור</th></tr></thead>
            <tbody>
              <tr><td><code>file</code></td><td>File</td><td>כן</td><td>קובץ הוידאו (mp4, mov, avi, mkv)</td></tr>
              <tr><td><code>title</code></td><td>String</td><td>לא</td><td>כותרת הסרטון</td></tr>
            </tbody>
          </table>

          <h4>תגובה</h4>
          <pre class="code-block"><span class="token-punctuation">{</span>
  <span class="token-string">"status"</span>: <span class="token-string">"success"</span>,
  <span class="token-string">"data"</span>: <span class="token-punctuation">{</span>
    <span class="token-string">"token"</span>: <span class="token-string">"abc123xyz"</span>,
    <span class="token-string">"title"</span>: <span class="token-string">"הסרטון שלי"</span>,
    <span class="token-string">"status"</span>: <span class="token-string">"processing"</span>
  <span class="token-punctuation">}</span>
<span class="token-punctuation">}</span></pre>

          <div class="docs-code-section">
            <div class="code-tabs">
              <span class="code-tab" :class="{ active: uploadTab === 'js' }" @click="uploadTab = 'js'">JavaScript</span>
              <span class="code-tab" :class="{ active: uploadTab === 'php' }" @click="uploadTab = 'php'">PHP</span>
              <span class="code-tab" :class="{ active: uploadTab === 'python' }" @click="uploadTab = 'python'">Python</span>
              <span class="code-tab" :class="{ active: uploadTab === 'curl' }" @click="uploadTab = 'curl'">cURL</span>
            </div>
            <div class="code-block" v-show="uploadTab === 'js'" v-html="examples.upload.js"></div>
            <div class="code-block" v-show="uploadTab === 'php'" v-html="examples.upload.php"></div>
            <div class="code-block" v-show="uploadTab === 'python'" v-html="examples.upload.python"></div>
            <div class="code-block" v-show="uploadTab === 'curl'" v-html="examples.upload.curl"></div>
          </div>
        </section>

        <!-- Chunked Upload -->
        <section id="chunked-upload" class="docs-section">
          <h2>העלאה מקוטעת (Chunked Upload)</h2>
          <p>לקבצים גדולים (מעל 100MB), מומלץ להשתמש בהעלאה מקוטעת. התהליך מתבצע ב-4 שלבים:</p>
          
          <div class="steps-mini">
            <div class="step-mini"><span class="step-num">1</span> אתחול (<code>chunk_init</code>)</div>
            <div class="step-mini"><span class="step-num">2</span> שליחת חלקים (<code>chunk_upload</code>)</div>
            <div class="step-mini"><span class="step-num">3</span> בדיקת סטטוס (<code>chunk_status</code>)</div>
            <div class="step-mini"><span class="step-num">4</span> השלמה (<code>chunk_complete</code>)</div>
          </div>

          <h3>שלב 1: אתחול</h3>
          <div class="endpoint-header">
            <span class="method-badge post">POST</span>
            <code class="endpoint-url">/video_manager/chunk_init</code>
          </div>
          <table class="params-table">
            <thead><tr><th>שם</th><th>סוג</th><th>חובה</th><th>תיאור</th></tr></thead>
            <tbody>
              <tr><td><code>filename</code></td><td>String</td><td>כן</td><td>שם הקובץ</td></tr>
              <tr><td><code>filesize</code></td><td>Number</td><td>כן</td><td>גודל הקובץ בבתים</td></tr>
              <tr><td><code>title</code></td><td>String</td><td>לא</td><td>כותרת הסרטון</td></tr>
            </tbody>
          </table>

          <h3>שלב 2: שליחת חלקים</h3>
          <div class="endpoint-header">
            <span class="method-badge post">POST</span>
            <code class="endpoint-url">/video_manager/chunk_upload</code>
          </div>
          <p>שלחו כל חלק כ-binary data עם הכותרות הבאות:</p>
          <table class="params-table">
            <thead><tr><th>כותרת (Header)</th><th>תיאור</th></tr></thead>
            <tbody>
              <tr><td><code>X-Upload-ID</code></td><td>מזהה ההעלאה מהשלב הקודם</td></tr>
              <tr><td><code>X-Chunk-Index</code></td><td>מספר סידורי של החלק (מתחיל מ-0)</td></tr>
              <tr><td><code>X-Chunk-MD5</code></td><td>MD5 hash של החלק לוידוא שלמות</td></tr>
            </tbody>
          </table>

          <h3>שלב 3: בדיקת סטטוס</h3>
          <div class="endpoint-header">
            <span class="method-badge get">GET</span>
            <code class="endpoint-url">/video_manager/chunk_status?upload_id=UPLOAD_ID</code>
          </div>

          <h3>שלב 4: השלמה</h3>
          <div class="endpoint-header">
            <span class="method-badge post">POST</span>
            <code class="endpoint-url">/video_manager/chunk_complete</code>
          </div>
          <table class="params-table">
            <thead><tr><th>שם</th><th>סוג</th><th>חובה</th><th>תיאור</th></tr></thead>
            <tbody>
              <tr><td><code>upload_id</code></td><td>String</td><td>כן</td><td>מזהה ההעלאה</td></tr>
            </tbody>
          </table>
        </section>

        <!-- List Videos -->
        <section id="list-videos" class="docs-section">
          <h2>רשימת סרטונים</h2>
          <div class="endpoint-header">
            <span class="method-badge get">GET</span>
            <code class="endpoint-url">/video_manager/results</code>
          </div>
          <p>קבלת רשימת הסרטונים בחשבון עם תמיכה בדפדוף.</p>
          
          <h4>פרמטרים (Query)</h4>
          <table class="params-table">
            <thead><tr><th>שם</th><th>סוג</th><th>חובה</th><th>תיאור</th></tr></thead>
            <tbody>
              <tr><td><code>page</code></td><td>Number</td><td>לא</td><td>מספר עמוד (ברירת מחדל: 0)</td></tr>
              <tr><td><code>limit</code></td><td>Number</td><td>לא</td><td>כמות תוצאות בעמוד (ברירת מחדל: 20)</td></tr>
            </tbody>
          </table>

          <h4>תגובה</h4>
          <pre class="code-block"><span class="token-punctuation">{</span>
  <span class="token-string">"status"</span>: <span class="token-string">"success"</span>,
  <span class="token-string">"data"</span>: <span class="token-punctuation">{</span>
    <span class="token-string">"items"</span>: <span class="token-punctuation">[</span>
      <span class="token-punctuation">{</span>
        <span class="token-string">"id"</span>: <span class="token-number">1</span>,
        <span class="token-string">"token"</span>: <span class="token-string">"abc123"</span>,
        <span class="token-string">"title"</span>: <span class="token-string">"הסרטון שלי"</span>,
        <span class="token-string">"status"</span>: <span class="token-string">"public"</span>,
        <span class="token-string">"pic_url"</span>: <span class="token-string">"https://..."</span>,
        <span class="token-string">"duration"</span>: <span class="token-number">120</span>,
        <span class="token-string">"size"</span>: <span class="token-number">15728640</span>
      <span class="token-punctuation">}</span>
    <span class="token-punctuation">]</span>,
    <span class="token-string">"num"</span>: <span class="token-number">42</span>
  <span class="token-punctuation">}</span>
<span class="token-punctuation">}</span></pre>

          <div class="docs-code-section">
            <div class="code-tabs">
              <span class="code-tab" :class="{ active: listTab === 'js' }" @click="listTab = 'js'">JavaScript</span>
              <span class="code-tab" :class="{ active: listTab === 'php' }" @click="listTab = 'php'">PHP</span>
              <span class="code-tab" :class="{ active: listTab === 'python' }" @click="listTab = 'python'">Python</span>
              <span class="code-tab" :class="{ active: listTab === 'curl' }" @click="listTab = 'curl'">cURL</span>
            </div>
            <div class="code-block" v-show="listTab === 'js'" v-html="examples.list.js"></div>
            <div class="code-block" v-show="listTab === 'php'" v-html="examples.list.php"></div>
            <div class="code-block" v-show="listTab === 'python'" v-html="examples.list.python"></div>
            <div class="code-block" v-show="listTab === 'curl'" v-html="examples.list.curl"></div>
          </div>
        </section>

        <!-- Video Details -->
        <section id="video-details" class="docs-section">
          <h2>פרטי סרטון</h2>
          <div class="endpoint-header">
            <span class="method-badge get">GET</span>
            <code class="endpoint-url">/video_manager/details</code>
          </div>
          <p>קבלת מידע מפורט על סרטון ספציפי לפי טוקן.</p>
          
          <h4>פרמטרים (Query)</h4>
          <table class="params-table">
            <thead><tr><th>שם</th><th>סוג</th><th>חובה</th><th>תיאור</th></tr></thead>
            <tbody>
              <tr><td><code>token</code></td><td>String</td><td>כן</td><td>טוקן הסרטון</td></tr>
            </tbody>
          </table>

          <h4>תגובה</h4>
          <pre class="code-block"><span class="token-punctuation">{</span>
  <span class="token-string">"status"</span>: <span class="token-string">"success"</span>,
  <span class="token-string">"data"</span>: <span class="token-punctuation">{</span>
    <span class="token-string">"id"</span>: <span class="token-number">1</span>,
    <span class="token-string">"token"</span>: <span class="token-string">"abc123"</span>,
    <span class="token-string">"title"</span>: <span class="token-string">"הסרטון שלי"</span>,
    <span class="token-string">"status"</span>: <span class="token-string">"public"</span>,
    <span class="token-string">"pic_url"</span>: <span class="token-string">"https://..."</span>,
    <span class="token-string">"embed_url"</span>: <span class="token-string">"https://embed.wizzo.stream/abc123"</span>,
    <span class="token-string">"duration"</span>: <span class="token-number">120</span>,
    <span class="token-string">"size"</span>: <span class="token-number">15728640</span>,
    <span class="token-string">"date_created"</span>: <span class="token-string">"2025-01-15T10:30:00Z"</span>
  <span class="token-punctuation">}</span>
<span class="token-punctuation">}</span></pre>
        </section>

        <!-- Update Video -->
        <section id="update-video" class="docs-section">
          <h2>עדכון סרטון</h2>
          <div class="endpoint-header">
            <span class="method-badge post">POST</span>
            <code class="endpoint-url">/video_manager/update</code>
          </div>
          <p>עדכון פרטי סרטון קיים.</p>
          
          <h4>פרמטרים (Body)</h4>
          <table class="params-table">
            <thead><tr><th>שם</th><th>סוג</th><th>חובה</th><th>תיאור</th></tr></thead>
            <tbody>
              <tr><td><code>token</code></td><td>String</td><td>כן</td><td>טוקן הסרטון</td></tr>
              <tr><td><code>title</code></td><td>String</td><td>לא</td><td>כותרת חדשה</td></tr>
              <tr><td><code>status</code></td><td>String</td><td>לא</td><td>סטטוס: public / private / unlisted</td></tr>
            </tbody>
          </table>
        </section>

        <!-- Delete Video -->
        <section id="delete-video" class="docs-section">
          <h2>מחיקת סרטון</h2>
          <div class="endpoint-header">
            <span class="method-badge post">POST</span>
            <code class="endpoint-url">/video_manager/remove</code>
          </div>
          <p>מחיקה לצמיתות של סרטון.</p>
          
          <h4>פרמטרים (Body)</h4>
          <table class="params-table">
            <thead><tr><th>שם</th><th>סוג</th><th>חובה</th><th>תיאור</th></tr></thead>
            <tbody>
              <tr><td><code>token</code></td><td>String</td><td>כן</td><td>טוקן הסרטון למחיקה</td></tr>
            </tbody>
          </table>
        </section>

        <!-- Embed -->
        <section id="embed" class="docs-section">
          <h2>הטמעת נגן</h2>
          <p>לאחר העלאת סרטון, ניתן להטמיע אותו באתר באמצעות iframe:</p>
          <pre class="code-block">&lt;<span class="token-keyword">iframe</span>
  <span class="token-property">src</span>=<span class="token-string">"https://embed.wizzo.stream/VIDEO_TOKEN"</span>
  <span class="token-property">width</span>=<span class="token-string">"100%"</span>
  <span class="token-property">height</span>=<span class="token-string">"100%"</span>
  <span class="token-property">frameborder</span>=<span class="token-string">"0"</span>
  <span class="token-property">allowfullscreen</span>
&gt;&lt;/<span class="token-keyword">iframe</span>&gt;</pre>
          <p>לשילוב רספונסיבי, עטפו את ה-iframe ב-container עם יחס 16:9:</p>
          <pre class="code-block">&lt;<span class="token-keyword">div</span> <span class="token-property">style</span>=<span class="token-string">"position:relative;padding-top:56.25%"</span>&gt;
  &lt;<span class="token-keyword">iframe</span>
    <span class="token-property">src</span>=<span class="token-string">"https://embed.wizzo.stream/VIDEO_TOKEN"</span>
    <span class="token-property">style</span>=<span class="token-string">"position:absolute;top:0;left:0;width:100%;height:100%"</span>
    <span class="token-property">frameborder</span>=<span class="token-string">"0"</span>
    <span class="token-property">allowfullscreen</span>
  &gt;&lt;/<span class="token-keyword">iframe</span>&gt;
&lt;/<span class="token-keyword">div</span>&gt;</pre>
        </section>

        <!-- Webhooks -->
        <section id="webhooks" class="docs-section">
          <h2>Webhooks</h2>
          <p>הגדירו כתובת Callback URL בדשבורד כדי לקבל עדכונים אוטומטיים כאשר סרטון מסיים עיבוד. ה-webhook נשלח כ-POST request לכתובת שהגדרתם.</p>
          
          <h4>גוף הבקשה שתשלח אליכם</h4>
          <pre class="code-block"><span class="token-punctuation">{</span>
  <span class="token-string">"event"</span>: <span class="token-string">"video.processed"</span>,
  <span class="token-string">"data"</span>: <span class="token-punctuation">{</span>
    <span class="token-string">"token"</span>: <span class="token-string">"abc123"</span>,
    <span class="token-string">"status"</span>: <span class="token-string">"ready"</span>,
    <span class="token-string">"embed_url"</span>: <span class="token-string">"https://embed.wizzo.stream/abc123"</span>
  <span class="token-punctuation">}</span>
<span class="token-punctuation">}</span></pre>
          <p>ניתן להגדיר מספר כתובות Callback בו-זמנית מתוך עמוד ה-API Keys בדשבורד.</p>
        </section>

      </div>
    </main>
  </div>
</template>

<script>
export default {
  name: 'DocsView',
  data() {
    return {
      sidebarOpen: false,
      activeSection: 'introduction',
      authTab: 'js',
      uploadTab: 'js',
      listTab: 'js',
      navSections: [
        { id: 'introduction', label: 'מבוא', icon: 'fas fa-home' },
        { id: 'authentication', label: 'אימות', icon: 'fas fa-key' },
        { id: 'upload', label: 'העלאת סרטון', icon: 'fas fa-cloud-upload-alt' },
        { id: 'chunked-upload', label: 'העלאה מקוטעת', icon: 'fas fa-puzzle-piece' },
        { id: 'list-videos', label: 'רשימת סרטונים', icon: 'fas fa-list' },
        { id: 'video-details', label: 'פרטי סרטון', icon: 'fas fa-info-circle' },
        { id: 'update-video', label: 'עדכון סרטון', icon: 'fas fa-edit' },
        { id: 'delete-video', label: 'מחיקת סרטון', icon: 'fas fa-trash' },
        { id: 'embed', label: 'הטמעת נגן', icon: 'fas fa-play-circle' },
        { id: 'webhooks', label: 'Webhooks', icon: 'fas fa-bell' },
      ],
      examples: {
        upload: {
          js: `<span class="token-keyword">const</span> <span class="token-variable">formData</span> = <span class="token-keyword">new</span> <span class="token-function">FormData</span>();
<span class="token-variable">formData</span>.<span class="token-method">append</span>(<span class="token-string">'file'</span>, <span class="token-variable">videoFile</span>);
<span class="token-variable">formData</span>.<span class="token-method">append</span>(<span class="token-string">'title'</span>, <span class="token-string">'הסרטון שלי'</span>);

<span class="token-keyword">const</span> <span class="token-variable">response</span> = <span class="token-keyword">await</span> <span class="token-function">fetch</span>(
  <span class="token-string">'https://api.wizzo.stream/api_wizzocms/video_manager/upload'</span>,
  {
    <span class="token-property">method</span>: <span class="token-string">'POST'</span>,
    <span class="token-property">headers</span>: { <span class="token-string">'Authorization'</span>: <span class="token-string">\`Bearer \${API_KEY}\`</span> },
    <span class="token-property">body</span>: <span class="token-variable">formData</span>
  }
);
<span class="token-keyword">const</span> <span class="token-variable">data</span> = <span class="token-keyword">await</span> <span class="token-variable">response</span>.<span class="token-method">json</span>();
<span class="token-variable">console</span>.<span class="token-method">log</span>(<span class="token-variable">data</span>.<span class="token-property">data</span>.<span class="token-property">token</span>);`,

          php: `<span class="token-variable">$curl</span> = <span class="token-function">curl_init</span>();
<span class="token-function">curl_setopt_array</span>(<span class="token-variable">$curl</span>, [
  <span class="token-property">CURLOPT_URL</span> => <span class="token-string">'https://api.wizzo.stream/api_wizzocms/video_manager/upload'</span>,
  <span class="token-property">CURLOPT_POST</span> => <span class="token-keyword">true</span>,
  <span class="token-property">CURLOPT_RETURNTRANSFER</span> => <span class="token-keyword">true</span>,
  <span class="token-property">CURLOPT_HTTPHEADER</span> => [<span class="token-string">'Authorization: Bearer '</span> . <span class="token-variable">$apiKey</span>],
  <span class="token-property">CURLOPT_POSTFIELDS</span> => [
    <span class="token-string">'file'</span> => <span class="token-keyword">new</span> <span class="token-function">CURLFile</span>(<span class="token-variable">$filePath</span>),
    <span class="token-string">'title'</span> => <span class="token-string">'הסרטון שלי'</span>
  ]
]);
<span class="token-variable">$response</span> = <span class="token-function">curl_exec</span>(<span class="token-variable">$curl</span>);
<span class="token-variable">$data</span> = <span class="token-function">json_decode</span>(<span class="token-variable">$response</span>, <span class="token-keyword">true</span>);`,

          python: `<span class="token-keyword">import</span> <span class="token-variable">requests</span>

<span class="token-variable">response</span> = <span class="token-variable">requests</span>.<span class="token-method">post</span>(
  <span class="token-string">'https://api.wizzo.stream/api_wizzocms/video_manager/upload'</span>,
  <span class="token-property">headers</span>={<span class="token-string">'Authorization'</span>: <span class="token-string">f'Bearer {api_key}'</span>},
  <span class="token-property">files</span>={<span class="token-string">'file'</span>: <span class="token-function">open</span>(<span class="token-string">'video.mp4'</span>, <span class="token-string">'rb'</span>)},
  <span class="token-property">data</span>={<span class="token-string">'title'</span>: <span class="token-string">'הסרטון שלי'</span>}
)
<span class="token-variable">data</span> = <span class="token-variable">response</span>.<span class="token-method">json</span>()`,

          curl: `<span class="token-function">curl</span> -X POST \\
  <span class="token-string">'https://api.wizzo.stream/api_wizzocms/video_manager/upload'</span> \\
  -H <span class="token-string">'Authorization: Bearer YOUR_API_KEY'</span> \\
  -F <span class="token-string">'file=@video.mp4'</span> \\
  -F <span class="token-string">'title=הסרטון שלי'</span>`
        },
        list: {
          js: `<span class="token-keyword">const</span> <span class="token-variable">response</span> = <span class="token-keyword">await</span> <span class="token-function">fetch</span>(
  <span class="token-string">'https://api.wizzo.stream/api_wizzocms/video_manager/results?page=0&limit=20'</span>,
  {
    <span class="token-property">headers</span>: { <span class="token-string">'Authorization'</span>: <span class="token-string">\`Bearer \${API_KEY}\`</span> }
  }
);
<span class="token-keyword">const</span> <span class="token-variable">data</span> = <span class="token-keyword">await</span> <span class="token-variable">response</span>.<span class="token-method">json</span>();
<span class="token-variable">console</span>.<span class="token-method">log</span>(<span class="token-variable">data</span>.<span class="token-property">data</span>.<span class="token-property">items</span>); <span class="token-comment">// Array of videos</span>`,

          php: `<span class="token-variable">$curl</span> = <span class="token-function">curl_init</span>();
<span class="token-function">curl_setopt_array</span>(<span class="token-variable">$curl</span>, [
  <span class="token-property">CURLOPT_URL</span> => <span class="token-string">'https://api.wizzo.stream/api_wizzocms/video_manager/results?page=0&limit=20'</span>,
  <span class="token-property">CURLOPT_RETURNTRANSFER</span> => <span class="token-keyword">true</span>,
  <span class="token-property">CURLOPT_HTTPHEADER</span> => [<span class="token-string">'Authorization: Bearer '</span> . <span class="token-variable">$apiKey</span>]
]);
<span class="token-variable">$response</span> = <span class="token-function">curl_exec</span>(<span class="token-variable">$curl</span>);
<span class="token-variable">$data</span> = <span class="token-function">json_decode</span>(<span class="token-variable">$response</span>, <span class="token-keyword">true</span>);`,

          python: `<span class="token-variable">response</span> = <span class="token-variable">requests</span>.<span class="token-method">get</span>(
  <span class="token-string">'https://api.wizzo.stream/api_wizzocms/video_manager/results'</span>,
  <span class="token-property">headers</span>={<span class="token-string">'Authorization'</span>: <span class="token-string">f'Bearer {api_key}'</span>},
  <span class="token-property">params</span>={<span class="token-string">'page'</span>: <span class="token-number">0</span>, <span class="token-string">'limit'</span>: <span class="token-number">20</span>}
)
<span class="token-variable">videos</span> = <span class="token-variable">response</span>.<span class="token-method">json</span>()[<span class="token-string">'data'</span>][<span class="token-string">'items'</span>]`,

          curl: `<span class="token-function">curl</span> -X GET \\
  <span class="token-string">'https://api.wizzo.stream/api_wizzocms/video_manager/results?page=0&limit=20'</span> \\
  -H <span class="token-string">'Authorization: Bearer YOUR_API_KEY'</span>`
        }
      }
    }
  },
  mounted() {
    window.addEventListener('scroll', this.updateActiveSection);
    this.updateActiveSection();
  },
  unmounted() {
    window.removeEventListener('scroll', this.updateActiveSection);
  },
  methods: {
    updateActiveSection() {
      const sections = this.navSections.map(s => document.getElementById(s.id)).filter(Boolean);
      const scrollY = window.scrollY + 120;
      for (let i = sections.length - 1; i >= 0; i--) {
        if (sections[i].offsetTop <= scrollY) {
          this.activeSection = sections[i].id;
          return;
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.docs-page { display: flex; min-height: 100vh; background: var(--navy-deep); direction: rtl; }

/* Sidebar */
.docs-sidebar { width: 280px; position: fixed; top: 0; bottom: 0; right: 0; background: var(--navy-mid); border-left: 1px solid rgba(255,255,255,0.06); display: flex; flex-direction: column; z-index: 100; overflow-y: auto;
  @media (max-width: 768px) { transform: translateX(100%); transition: transform 0.3s ease;
    &.open { transform: translateX(0); }
  }
}
.docs-sidebar-header { padding: 24px; display: flex; align-items: center; gap: 12px; border-bottom: 1px solid rgba(255,255,255,0.06);
  .logo { width: 120px; height: 36px; display: block; }
}
.docs-nav { flex: 1; padding: 16px 12px;
  .docs-nav-item { display: flex; align-items: center; gap: 10px; padding: 10px 14px; border-radius: var(--radius_xs); color: var(--text-muted); font-size: 14px; transition: all 0.15s; margin-bottom: 2px;
    .nav-icon { width: 18px; text-align: center; font-size: 13px; }
    &:hover { background: rgba(255,255,255,0.05); color: var(--text-light); }
    &.active { background: var(--color1-dim); color: var(--color1); font-weight: 600; }
  }
}
.docs-sidebar-footer { padding: 16px 20px; border-top: 1px solid rgba(255,255,255,0.06); }

.docs-overlay { display: none;
  @media (max-width: 768px) { &.open { display: block; position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 99; } }
}

/* Topbar mobile */
.docs-topbar { position: sticky; top: 0; background: var(--navy-mid); padding: 14px 20px; display: flex; align-items: center; gap: 16px; border-bottom: 1px solid rgba(255,255,255,0.06); z-index: 50;
  .docs-menu-btn { background: none; border: none; color: var(--text-light); font-size: 20px; cursor: pointer; }
  .docs-topbar-title { font-family: var(--font-mono); font-size: 14px; color: var(--text-muted); }
}

/* Main content */
.docs-main { flex: 1; margin-right: 280px;
  @media (max-width: 768px) { margin-right: 0; }
}
.docs-content { max-width: 860px; padding: 48px 48px 100px;
  @media (max-width: 768px) { padding: 24px 20px 80px; }
}

/* Sections */
.docs-section { margin-bottom: 64px; padding-top: 20px;
  h1 { font-size: var(--h1); font-weight: 800; color: #fff; margin-bottom: 16px; }
  h2 { font-size: var(--h2); font-weight: 700; color: #fff; margin-bottom: 16px; padding-top: 8px; border-top: 1px solid rgba(255,255,255,0.06); }
  h3 { font-size: var(--h3); font-weight: 600; color: var(--text-light); margin: 28px 0 12px; }
  h4 { font-size: var(--h4); font-weight: 600; color: var(--text-light); margin: 24px 0 12px; }
  p { font-size: var(--h5); color: var(--text-muted); line-height: 1.7; margin-bottom: 16px;
    code { background: rgba(255,255,255,0.08); padding: 2px 8px; border-radius: 4px; font-family: var(--font-mono); font-size: 13px; color: var(--accent-blue); }
    a { color: var(--color1); text-decoration: underline; }
  }
  .docs-intro { font-size: var(--h4); }
}

/* Info box */
.docs-info-box { background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08); border-radius: var(--radius_xs); padding: 20px; margin-top: 20px;
  .info-row { display: flex; align-items: center; gap: 16px; padding: 10px 0; border-bottom: 1px solid rgba(255,255,255,0.04);
    &:last-child { border-bottom: none; }
    .info-label { font-size: 13px; font-weight: 600; color: var(--text-muted); min-width: 140px; }
    .info-value { font-family: var(--font-mono); font-size: 13px; color: var(--color1); background: rgba(46,247,186,0.06); padding: 4px 10px; border-radius: 4px; }
  }
}

/* Endpoint header */
.endpoint-header { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; padding: 12px 16px; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06); border-radius: var(--radius_xs);
  .endpoint-url { font-family: var(--font-mono); font-size: 15px; color: var(--text-light); }
}

/* Params table */
.params-table { width: 100%; border-collapse: collapse; margin-bottom: 24px; font-size: 14px;
  th { text-align: right; padding: 10px 14px; background: rgba(255,255,255,0.04); color: var(--text-muted); font-weight: 600; border-bottom: 1px solid rgba(255,255,255,0.06); }
  td { padding: 10px 14px; color: var(--text-light); border-bottom: 1px solid rgba(255,255,255,0.04);
    code { font-family: var(--font-mono); font-size: 13px; color: var(--accent-blue); background: rgba(255,255,255,0.06); padding: 2px 6px; border-radius: 3px; }
  }
}

/* Code section */
.docs-code-section { margin-top: 16px; border: 1px solid var(--code-border); border-radius: var(--radius_xs); overflow: hidden;
  .code-tabs { background: #161B22; }
  .code-block { border: none; border-radius: 0; margin: 0; }
}

/* Chunked upload steps */
.steps-mini { display: flex; flex-wrap: wrap; gap: 12px; margin: 20px 0 30px;
  .step-mini { display: flex; align-items: center; gap: 10px; padding: 10px 18px; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: var(--radius_xs); font-size: 14px; color: var(--text-light);
    .step-num { width: 24px; height: 24px; background: var(--color1); color: var(--navy-deep); border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 12px; }
    code { font-family: var(--font-mono); font-size: 13px; color: var(--accent-blue); }
  }
}
</style>
